/**
 * @fileoverview Drag-to-select time-range hook for the day / week time grids.
 *
 * Gives the calendar's day and week views a Notion / Google Calendar-style
 * interaction: press on an empty slot and drag vertically to sweep out a time
 * range, then release to open the "create" panel pre-filled with that
 * start + end time. A plain click (no drag) still works — it resolves to a
 * default one-hour slot, preserving the previous click-to-create behaviour.
 *
 * The grid is a linear pixel↔minute mapping: the top of the grid is
 * `firstHour`, and every hour is `hourHeightPx` tall. Selections snap to
 * `snapMinutes` (15) and are clamped to the visible window.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface GridSelection {
  /** Identifies which column (day) the active selection belongs to. */
  dayKey: string;
  /** Minutes-from-midnight for the selection start (snapped). */
  startMin: number;
  /** Minutes-from-midnight for the selection end (snapped). */
  endMin: number;
}

interface Options {
  /** First visible hour (top of the grid, e.g. 7). */
  firstHour: number;
  /** Number of visible hours (grid height = firstHour..firstHour+totalHours). */
  totalHours: number;
  /** Pixel height of a single hour row. */
  hourHeightPx: number;
  /** Snap granularity in minutes (default 15). */
  snapMinutes?: number;
  /** Default duration (minutes) applied to a click / zero-length drag. */
  defaultDurationMin?: number;
  /** Fired on release with the resolved column + range. */
  onSelect: (dayKey: string, startMin: number, endMin: number) => void;
}

export interface TimeGridSelection {
  /** The live selection while dragging (for rendering the highlight). */
  selection: GridSelection | null;
  /** Attach to each column's interaction layer. */
  onColumnPointerDown: (e: React.MouseEvent, dayKey: string) => void;
  /** Convert minutes-from-midnight to a pixel offset within the grid. */
  minToPx: (min: number) => number;
}

export function useTimeGridSelection({
  firstHour,
  totalHours,
  hourHeightPx,
  snapMinutes = 15,
  defaultDurationMin = 60,
  onSelect,
}: Options): TimeGridSelection {
  const [selection, setSelection] = useState<GridSelection | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    dayKey: string;
    rectTop: number;
    anchorMin: number;
  } | null>(null);

  const minMin = firstHour * 60;
  const maxMin = (firstHour + totalHours) * 60;

  const pxToMin = useCallback(
    (px: number) => {
      const raw = firstHour * 60 + (px / hourHeightPx) * 60;
      const snapped = Math.round(raw / snapMinutes) * snapMinutes;
      return Math.min(maxMin, Math.max(minMin, snapped));
    },
    [firstHour, hourHeightPx, snapMinutes, maxMin, minMin],
  );

  const minToPx = useCallback(
    (min: number) => ((min - minMin) / 60) * hourHeightPx,
    [minMin, hourHeightPx],
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const cur = pxToMin(e.clientY - d.rectTop);
      setSelection({
        dayKey: d.dayKey,
        startMin: Math.min(d.anchorMin, cur),
        endMin: Math.max(d.anchorMin, cur),
      });
    };

    const handleUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      setSelection((sel) => {
        if (d && sel) {
          const { startMin } = sel;
          const endMin =
            sel.endMin - startMin < snapMinutes
              ? Math.min(maxMin, startMin + defaultDurationMin)
              : sel.endMin;
          onSelect(sel.dayKey, startMin, endMin);
        }
        return null;
      });
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging, pxToMin, snapMinutes, maxMin, defaultDurationMin, onSelect]);

  const onColumnPointerDown = useCallback(
    (e: React.MouseEvent, dayKey: string) => {
      if (e.button !== 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const anchor = pxToMin(e.clientY - rect.top);
      dragRef.current = { dayKey, rectTop: rect.top, anchorMin: anchor };
      setSelection({ dayKey, startMin: anchor, endMin: anchor });
      setDragging(true);
    },
    [pxToMin],
  );

  return { selection, onColumnPointerDown, minToPx };
}

/** Format minutes-from-midnight as a short 12-hour label (e.g. "9:15 am"). */
export function formatMinLabel(min: number): string {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const period = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}
